import React, { useEffect, useRef, useState } from "react"
import PropTypes from 'prop-types'
import Button from 'react-bootstrap/Button'
import { PhoneNumberUtil, PhoneNumberFormat } from 'google-libphonenumber'
import { v4 as uuidv4 } from 'uuid'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { Translate } from "../../Components/Translator.jsx"
import { IsEmptyString, GetCallHistory, LocalizedDateTime, BroadcastBuffered, OpenPopup, GetBrandButtonClass } from "../../Components/GeneralFunctions.jsx"

import CallHistory from "../../Components/CallHistory.jsx"
import SpeedDials from "../../Components/SpeedDials.jsx"

import CopyPng from '../../Assets/copy.png'
import PhoneBlackPng from '../../Assets/phoneblack.png'
import SavePng from '../../Assets/save.png'

var sforce

const EmbeddedSalesforce = (props, ref) => {

  const [mRepainter, SetRepainter] = useState("")

  const [mSalesforceTab, SetSalesforceTab] = useState(localStorage.getItem("SalesforceTab") || "Search")

  //Set to true when the salesforce opencti has been loaded
  const [mInitialized, SetInitialized] = useState(false) 

  //Because Salesforce does not support searching multiple phone number formats at once, we need to do multiple search requests 
  //Keeps track of which search queries with which numberformats we are still waiting for 
  //Used only by the primary tab for the call
  const [mWaitingForRecognitionFormats, SetWaitingForRecognitionFormats] = useState([])  
  
  //This value keeps track of which search results we have found 
  //Used only by the primary tab for the call
  const mRecognitionBuffer = useRef([])
  
  //Keeps track of which searches had how many results
  //Used only by the primary tab for the call
  const mRecognitionBufferInternationalFormat = useRef("")
  const mRecognitionBufferInternationalFormatCount = useRef(0)
  const mRecognitionBufferNationalFormat = useRef("")
  const mRecognitionBufferNationalFormatCount = useRef(0) 
  
  //CallCenter settings
  const [mCallCenterSettings, SetCallCenterSettings] = useState(undefined) //undefined means we're still getting it. null means we failed to get it
  const [mCallCenterSettingDefaultInboundCallLogTitle, SetCallCenterSettingDefaultInboundCallLogTitle] = useState("Inbound call from %phonenumber%, started %starttime%, talk duration %talkduration%") 
  const [mCallCenterSettingDefaultOutboundCallLogTitle, SetCallCenterSettingDefaultOutboundCallLogTitle] = useState("Outbound call to %phonenumber%, started %starttime%, talk duration %talkduration%") 
  const [mCallCenterSettingCallDispositions, SetCallCenterSettingCallDispositions] = useState([])
  const [mCallCenterSettingDefaultCallDisposition, SetCallCenterSettingDefaultCallDisposition] = useState("")
  const [mCallCenterSettingCallLogState, SetCallCenterSettingCallLogState] = useState("Completed")

  //Softphone layout
  const [mSoftphoneLayout, SetSoftphoneLayout] = useState(null)  

  //The values that are currenly being displayed in the calldisposition and log fields. Can be different for different frames.
  const [mCallDisposition, SetCallDisposition] = useState("")
  const [mLogMessage, SetLogMessage] = useState(localStorage.getItem("sforce_lastcalllogmessage"))

  const [mFrameOrigin, SetFrameOrigin] = useState("") 

  const [mBroadcastChannel, SetBroadcastChannel] = useState(new BroadcastChannel("cloudctiwebclient2024_salesforce"))

  useEffect(() => {
    mBroadcastChannel.addEventListener("message", function(event) {
      if (event.data === "repaint")
      {   
        setTimeout(() => {
          SetRepainter(uuidv4())
        }, 500)      
      }
      else if (event.data === "updatelogmessage")
      {
        SetLogMessage(localStorage.getItem("sforce_logmessagevalueid"))
      }
    });

    const windowUrl = window.location.search;
    var params = new URLSearchParams(windowUrl);
    const sfdcIframeOrigin = params.get("sfdcIframeOrigin")
    if (!IsEmptyString(sfdcIframeOrigin))
    {
      SetFrameOrigin(sfdcIframeOrigin)
    }
    else
    {
      console.log("Phone app does not have the sfdcIframeOrigin url parameter")
    }

    import('./opencti_min.js').then(() => {          
      window.sforce.opencti.initialize()
      SalesforceAPI_getCallCenterSettings()
      SalesforceAPI_getSoftphoneLayout()
      SalesforceAPI_enableClickToDial()
      SalesforceAPI_registerClickToDial()
      SetInitialized(true)
    })
  }, []);   

  //SALESFORCE API

  const SalesforceAPI_enableClickToDial = () => {
    window.sforce.opencti.enableClickToDial({
      callback: (response) => {
        if (response.success)
        {
          console.log('ClickToDial was enabled');
        }
        else
        {
          console.log('ClickToDial was not enabled');
        }
      }
    })
  }

  const SalesforceAPI_registerClickToDial = () => {
    window.sforce.opencti.onClickToDial({
      listener: (payload) => {
        console.log('ClickToDial', payload)
        props.OnMakeCall(payload.number, payload.recordName, "Salesforce", [{name: "Open " + payload.objectType, screenpopurl: SalesforceScreenpopUrlCreate(payload.objectType, payload.recordId)}])
      }}
    )
  }

  const SalesforceAPI_getCallCenterSettings = () => {
    window.sforce.opencti.getCallCenterSettings({
      callback: (response) => {
        if (response.success)
        {
          SetCallCenterSettings(response.returnValue)
          console.log('getCallCenterSettings', response.returnValue);

          const DefaultInboundCallLogTitle = response.returnValue["/WebclientSettings/DefaultInboundCallLogTitle"]
          if (!IsEmptyString(DefaultInboundCallLogTitle)) { SetCallCenterSettingDefaultInboundCallLogTitle(DefaultInboundCallLogTitle) }
          const DefaultOutboundCallLogTitle = response.returnValue["/WebclientSettings/DefaultOutboundCallLogTitle"]
          if (!IsEmptyString(DefaultOutboundCallLogTitle)) { SetCallCenterSettingDefaultOutboundCallLogTitle(DefaultOutboundCallLogTitle) }
          const CallDispositions = response.returnValue["/WebclientSettings/CallDispositionCodes"]
          try { if (!IsEmptyString(CallDispositions)) { SetCallCenterSettingCallDispositions(JSON.parse(CallDispositions)) } } catch {}
          const DefaultCallDisposition = response.returnValue["/WebclientSettings/DefaultCallDispositionCode"]
          if (!IsEmptyString(DefaultCallDisposition)) { SetCallCenterSettingDefaultCallDisposition(DefaultCallDisposition) }
          const CallLogState = response.returnValue["/WebclientSettings/CallLogState"]
          if (!IsEmptyString(CallLogState)) { SetCallCenterSettingCallLogState(CallLogState) } 
          
          //Save the default descriptions to the localstorage so we can show them as defaults in the settings
          if (!IsEmptyString(DefaultInboundCallLogTitle)) { localStorage.setItem("sforce_setting_defaultcallsubjectinbound", DefaultInboundCallLogTitle) }  
          if (!IsEmptyString(DefaultOutboundCallLogTitle)) { localStorage.setItem("sforce_setting_defaultcallsubjectoutbound", DefaultOutboundCallLogTitle) }       
        }
        else
        {
          SetCallCenterSettings(null)
          console.log('getCallCenterSettings error', response.errors);
        }
      }
    })
  }

  const SalesforceAPI_getSoftphoneLayout = () => 
  {
    window.sforce.opencti.getSoftphoneLayout({ 
      callback: ((response) => {
        SetSoftphoneLayout(response.returnValue)
        console.log("Salesforce API getSoftphoneLayout() response", response)
      })
    })
  }

  const SalesforceAPI_setSoftphonePanelVisibile = () => 
  {
    window.sforce.opencti.setSoftphonePanelVisibility({
      visible: true,
      callback: ((response) => {
        console.log("Salesforce API setSoftphonePanelVisibility() response", response)
      })
    })
  }

  const SalesforceAPI_saveLog = (log) => {
    window.sforce.opencti.saveLog({
      value: log,
      callback: (response) => {
        if (response.success) 
        {
          if (response.returnValue) 
          {
            localStorage.setItem("sforce_lastcalllogid", response.returnValue.recordId)
            console.log("Log set to recordId " + response.returnValue.recordId)
            Repaint()
          }
        }
        else 
        {
            console.log('Log failed',response);
        }
      }
    });
  }

  //This is always only called in the primary tab
  //Format is internationalformat, nationalformat or screenpop
  const SalesforceAPI_searchAndScreenpop = (number, inbound, format) => {
    window.sforce.opencti.searchAndScreenPop({
      searchParams: number, //.replace("+",""),
      //queryParams: 'example query params',
      defaultFieldValues: { Phone: number },
      callType: inbound ? window.sforce.opencti.CALL_TYPE.INBOUND : window.sforce.opencti.CALL_TYPE.OUTBOUND,
      deferred: (format !== "screenpop"),
      callback: ((response) => {
        console.log("Salesforce API searchAndGetScreenPopUrl() response", response)
        if (format !== "screenpop")
        {
          const WaitingForRecognitionFormats = mWaitingForRecognitionFormats.filter((item) => item !== format)
          SetWaitingForRecognitionFormats(WaitingForRecognitionFormats)

          const MatchesKeys = Object.keys(response.returnValue).filter((key) => key !== "SCREEN_POP_DATA")

          if (format === "internationalformat") 
          { 
            mRecognitionBufferInternationalFormatCount.current = MatchesKeys.length
          }
          
          if (format === "nationalformat") 
          { 
            mRecognitionBufferNationalFormatCount.current = MatchesKeys.length
          }

          const MatchFound = MatchesKeys.length > 0
          if (MatchFound) 
          {
            let Matches = []
            MatchesKeys.map((key) => Matches.push(response.returnValue[key]))

            Matches.map((match) => { 
              if (mRecognitionBuffer.current.filter((item) => item.Id === match.Id).length === 0)
              {
                mRecognitionBuffer.current.push(match) 
              }
            })
          }
          
          if (WaitingForRecognitionFormats.length === 0) 
          { 
            localStorage.setItem("sforce_waitingforrecognition", "0")         

            if (mRecognitionBuffer.current.length > 0)
            {
              localStorage.setItem("sforce_lastcallrecognition", JSON.stringify(mRecognitionBuffer.current)) 
              localStorage.setItem("sforce_lastcallrecognitionid", mRecognitionBuffer.current[0].Id)

              if (mRecognitionBuffer.current.length < 2)
              { 
                mRecognitionBufferInternationalFormatCount.current >= mRecognitionBufferNationalFormatCount.current ?
                  SalesforceAPI_searchAndScreenpop(mRecognitionBufferInternationalFormat.current,inbound,"screenpop") :
                  SalesforceAPI_searchAndScreenpop(mRecognitionBufferNationalFormat.current,inbound,"screenpop")
              }

              if ((mRecognitionBuffer.current.length === 1) && (localStorage.getItem("sforce_setting_registercall") === "1"))
              {
                let starttimeiso8601 = ""
                try
                {
                  const starttimeString = localStorage.getItem("sforce_lastcallstarttime")
                  const starttime = !IsEmptyString(starttimeString) ? parseInt(starttimeString,10) : null
                  starttimeiso8601 = starttime !== null ? new Date(starttime).toISOString() : ""
                } catch {}
            
                try
                {
                  const answeredtimeString = localStorage.getItem("sforce_lastcallansweredtime")
                  answeredtime = !IsEmptyString(answeredtimeString) ? parseInt(answeredtimeString,10) : null
                } catch {}
            
                let endedtime = null
                try
                {
                  const endedtimeString = localStorage.getItem("sforce_lastcallendedtime")
                  endedtime = !IsEmptyString(endedtimeString) ? parseInt(endedtimeString,10) : null
                } catch {}

                SetCallLog("",number,inbound,starttimeiso8601,answeredtime,endedtime,mRecognitionBuffer.current[0].Id,mRecognitionBuffer.current[0].RecordType)
              }
              
              SetCallHistoryRecognition()
            }
          }
          Repaint()
        }
      })
    });
  }

  const SalesforceAPI_screenpop = (url) => {
    window.sforce.opencti.screenPop({
      type: window.sforce.opencti.SCREENPOP_TYPE.URL,
      params: { url: url },
      callback: (response) => {
        if (response.success) {
          console.log('Screen pop successful');
        } else {
          console.error('Error with screen pop:', response.errors);
        }
      }
    });
  }

  

  const SalesforceAPI_showInConsole = () => {
      try 
      {
          window.sforce.opencti.setSoftphonePanelVisibility({
              visible: true,
              callback: (response) => {
              if (!response.success) 
              {
                console.error('Failed to set softphone panel visibility in Salesforce', response.errors)
              }
            }
          });
      }
      catch (error) 
      {
          console.log('Unable to set softphone panel visibility in Salesforce', error);
      }
  }

  const NormalizeCallFields = (aText) => {
    return aText.replace("%phonenumber%", "{{phonenumber}}")
      .replace("%direction%", "{{direction}}")
      .replace("%calldisposition%", "{{calldisposition}}")
      .replace("%starttime%", "{{starttime}}")
      .replace("%talktime%", "{{talktime}}")
      .replace("%talkduration%", "{{talktime}}")
  }

  //END OF SALESFORCE API  

  function BroadcastLogMessageBuffered(logMessage) 
  {
    BroadcastBuffered(mBroadcastChannel, "updatelogmessage", logMessage, "sforce_logmessagevalueid", "sforce_logmessagetimeoutid", 1000)
  }

  const GetDefaultCallSubject = (inbound) => {
    return inbound ? 
      (!IsEmptyString(localStorage.getItem("sforce_setting_defaultcallsubjectinbound")) ? NormalizeCallFields(localStorage.getItem("sforce_setting_defaultcallsubjectinbound")) : NormalizeCallFields(mCallCenterSettingDefaultInboundCallLogTitle)) :
      (!IsEmptyString(localStorage.getItem("sforce_setting_defaultcallsubjectoutbound")) ? NormalizeCallFields(localStorage.getItem("sforce_setting_defaultcallsubjectoutbound")) : NormalizeCallFields(mCallCenterSettingDefaultOutboundCallLogTitle))
  }

  const OnSelectedMatchChanged = (event) => 
  {
    localStorage.setItem("sforce_lastcallrecognitionid", event.target.value)
    SetCallHistoryRecognition()
    Repaint()
  }

  const OpenItemInSalesforce = (recordtype,id) => 
  {
    if (!IsEmptyString(mFrameOrigin))
    {
      SalesforceAPI_screenpop(mFrameOrigin + "/lightning/r/" + recordtype + "/" + id + "/view")
    }
    else
    {
      console.log("Unable to open item in Salesforce. Phone app does not have the sfdcIframeOrigin url parameter")
    }
  }

  const PerformSearch = (number,inbound) => 
  {
    try 
    {
      const userLocale = navigator.language || navigator.userLanguage; // e.g., "en-NL"
      const defaultRegion = userLocale.split('-')[1]; // Extract "NL"

      const phoneUtil = PhoneNumberUtil.getInstance()
      const parsedNumber = phoneUtil.parse(number, defaultRegion)
      mRecognitionBufferInternationalFormat.current = phoneUtil.format(parsedNumber, PhoneNumberFormat.E164)
      mRecognitionBufferNationalFormat.current = phoneUtil.format(parsedNumber, PhoneNumberFormat.NATIONAL)

      mRecognitionBuffer.current = []
      SetWaitingForRecognitionFormats(["internationalformat","nationalformat"])
      SalesforceAPI_searchAndScreenpop(`*${mRecognitionBufferInternationalFormat.current}*`, inbound, "internationalformat")
      SalesforceAPI_searchAndScreenpop(`*${mRecognitionBufferNationalFormat.current}*`, inbound, "nationalformat")
    } 
    catch (error) 
    {
        console.error('Failed to parse phone number:', error.message);
    }
  }

  const Repaint = () => 
  {
    console.log("Repaint")
    mBroadcastChannel.postMessage("repaint")
    SetRepainter(uuidv4())
  }

  const SalesforceScreenpopUrlCreate = (aRecordType, aIdentifier) => {
    return "embedded://salesforce." + aRecordType + "." + aIdentifier
  }

  const SalesforceScreenpopUrlExecute = (aUrl) => {
    const prefix = "embedded://";
    if (!aUrl.startsWith(prefix)) 
    {
      //This is a regular script, perhaps created earlier
      OpenPopup(aUrl)
    }
    else
    {
      const parts = aUrl.slice(prefix.length).split(".");
      if (parts.length > 0) 
      {
        if (parts[0] === "salesforce")
        {
          if (parts.length === 3)
          { 
            OpenItemInSalesforce(parts[1],parts[2])
          }
          else
          {
            console.log("Invalid popup value: " + aUrl)
          }
        }
        else
        {
          console.log("Salesforce Embedded cannot execute scripts for embedded " + parts[0])
        }
      }
      else
      {
        console.log("Empty popup value")
      }
    }
  }

  const SalesforceTabChange = (aTab) =>
  {
    SetSalesforceTab(aTab)
    localStorage.setItem("SalesforceTab",aTab)
  }

  const SecondsToShortTime = (seconds) => 
  {
    if (seconds === null) { return "0:00" }
    const Minutes = Math.floor(seconds / 60)
    const SecondsWithoutMinutes = (seconds - (60 * Minutes))
    return Minutes.toString() + ":" + (SecondsWithoutMinutes < 10 ? "0" : "") + SecondsWithoutMinutes.toString();
  }

  const SetCallHistoryRecognition = () =>
  {
    try
    {
        const ActiveCallString = localStorage.getItem("newestcallevent")
        const ActiveCall = !IsEmptyString(ActiveCallString) ? JSON.parse(ActiveCallString) : null
        if (ActiveCall != null)
        {
            const RecognitionBufferString = localStorage.getItem("sforce_lastcallrecognition") 
            const RecognitionBuffer = !IsEmptyString(RecognitionBufferString) ? JSON.parse(RecognitionBufferString) : null
            if ((RecognitionBuffer != null) && (RecognitionBuffer.length > 0))
            {
                const RecognitionBufferId = localStorage.getItem("sforce_lastcallrecognitionid")

                let Name = RecognitionBuffer[0].Name
                let Type = RecognitionBuffer[0].RecordType
                RecognitionBuffer.map((item) => { if (item.Id === RecognitionBufferId)
                  {
                    Type = item.RecordType
                    Name = item.Name
                  }
                }) 

                const script = {ScreenpopUrl: SalesforceScreenpopUrlCreate(Type,RecognitionBufferId), Name: "Open " + Type}

                props.OnSetHistoryCallRecognition(ActiveCall.CallId, Name, "Salesforce", [script])
            }
        }
    }
    catch {}
  }

  const SetCallLog = (aLastLogId,aNumber,aCallInbound,aStartTimeISO8601,aAnsweredTime,aEndedTime,aRecognitionId,aRecognitionType) =>
  {
    const talktimeseconds = aAnsweredTime !== null && aEndedTime !== null ? Math.floor((aEndedTime - aAnsweredTime) / 1000) : null 
    const talktime = SecondsToShortTime(talktimeseconds) 

    let subject = GetDefaultCallSubject(aCallInbound) 
    subject = IsEmptyString(subject) ? "Phone call" : subject.replace("{{phonenumber}}", aNumber)
        .replace("{{direction}}", aCallInbound ? Translate("Incoming") : Translate("Outgoing"))
        .replace("{{calldisposition}}", mCallDisposition)
        .replace("{{starttime}}", aStartTimeISO8601)
        .replace("{{talktime}}", talktime)

    let log = {
      ActivityDate: aStartTimeISO8601, 
      CallDisposition: !IsEmptyString(mCallDisposition) ? mCallDisposition : mCallCenterSettingDefaultCallDisposition,
      CallDurationInSeconds: talktimeseconds,  
      CallType: aCallInbound ? window.sforce.opencti.CALL_TYPE.INBOUND : window.sforce.opencti.CALL_TYPE.OUTBOUND, 
      Description: mLogMessage,
      Status: mCallCenterSettingCallLogState, 
      Subject: subject, 
      TaskSubtype: "Call" 
    }

    if (IsEmptyString(aLastLogId))
    {
      //Create new task
      log.entityApiName = "Task"
      log.WhoId = aRecognitionType === "Contact" || aRecognitionType === "Lead" ? aRecognitionId : ""
      log.WhatId = aRecognitionType === "Account" || aRecognitionType === "PersonAccount" || aRecognitionType === "cxsrec__cxsCandidate__c" ? aRecognitionId : ""
    }
    else
    {
      //Update existing task
      log.Id = aLastLogId  
      log.WhoId = aRecognitionType === "Contact" || aRecognitionType === "Lead" ? aRecognitionId : ""
      log.WhatId = aRecognitionType === "Account" || aRecognitionType === "PersonAccount" || aRecognitionType === "cxsrec__cxsCandidate__c" ? aRecognitionId : ""
    }

    console.log("SetCallLog",log)
    SalesforceAPI_saveLog(log)
    localStorage.setItem("sforce_lastcalllogmessage", mLogMessage)
  }

  //PAINT

  const PaintMultipleRecognition = (aNumber,aCallInbound,aStartTimeISO8601,aAnsweredTime,aEndedTime,aRecognition,aRecognitionId,aRecognitionTypes,aLastLogId) => 
  {
    const SelectedRecognitionType = aRecognitionTypes[aRecognitionId]
    const SelectedCallDisposition = !IsEmptyString(mCallDisposition) ? mCallDisposition : mCallCenterSettingDefaultCallDisposition

    return <div>
      <b>{aCallInbound ? Translate("Caller") : Translate("Called")}</b>
      <br/>  
      <select style={{ width: '100%', padding: '6px' }} onChange={OnSelectedMatchChanged}>
        {aRecognition.map((item) => <option selected={item.Id === aRecognitionId} style={{ padding: '6px' }} value={item.Id}>{item.Name}</option>)}
      </select>
      <Button variant="info" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => OpenItemInSalesforce(aRecognitionTypes[aRecognitionId], aRecognitionId)}>View</Button>
      <br/><br/>
      <b>Call Disposition</b><br/>
      <select style={{ width: '100%', padding: '6px' }}>
      {mCallCenterSettingCallDispositions.map((calldisposition) => <option selected={calldisposition === SelectedCallDisposition} style={{ padding: '6px' }} value={calldisposition}>{calldisposition}</option>)}
      </select>
      <br/>
      <textarea style={{ width: '100%', height: '100px', padding: '6px', marginTop: '6px' }} value={mLogMessage} onChange={(evt) => { SetLogMessage(evt.target.value); BroadcastLogMessageBuffered(evt.target.value); }}/>
      {!IsEmptyString(aLastLogId) ?

        <table width="100%">
          <tbody>
            <tr>
              <td width="50%" align="left"><Button variant="warning" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => SetCallLog(aLastLogId,aNumber,aCallInbound,aStartTimeISO8601,aAnsweredTime,aEndedTime,aRecognitionId,SelectedRecognitionType)}><img src={SavePng} style={{ height: '20px', paddingBottom: '2px'}}/> Update</Button></td>
              <td width="50%" align="right"><Button variant="info" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => OpenItemInSalesforce("Task", aLastLogId)}>View</Button></td>
            </tr>
          </tbody>
        </table> :

        <Button variant="info" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => SetCallLog("",aNumber,aCallInbound,aStartTimeISO8601,aAnsweredTime,aEndedTime,aRecognitionId,SelectedRecognitionType)}><img src={SavePng} style={{ height: '20px', paddingBottom: '2px'}}/> Register call</Button>
      }
    </div>
  }

  const PaintSingleRecognition = (aNumber,aCallInbound,aStartTimeISO8601,aAnsweredTime,aEndedTime,aRecognitionId,aRecognitionTypes,aRecognitionNames,aLastLogId) => 
  {
    const RecognitionType = aRecognitionTypes[aRecognitionId]
    const RecognitionName = aRecognitionNames[aRecognitionId]

    const SelectedCallDisposition = !IsEmptyString(mCallDisposition) ? mCallDisposition : mCallCenterSettingDefaultCallDisposition

    return <div>
      <b>{aCallInbound ? Translate("Caller") : Translate("Called")}</b><br/>   
      <Button variant="info" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => OpenItemInSalesforce(RecognitionType, aRecognitionId)}>{RecognitionName}</Button>
      <br/><br/>
      <b>Call Disposition</b><br/>
      <select style={{ width: '100%', padding: '6px' }} onChange={(event) => { SetCallDisposition(event.target.value) }}>
      {mCallCenterSettingCallDispositions.map((calldisposition) => <option selected={calldisposition === SelectedCallDisposition} style={{ padding: '6px' }} value={calldisposition}>{calldisposition}</option>)}
      </select>
      <br/>
      <textarea style={{ width: '100%', height: '100px', padding: '6px', marginTop: '6px' }} value={mLogMessage} onChange={(evt) => { SetLogMessage(evt.target.value); BroadcastLogMessageBuffered(evt.target.value); }}/>
      {!IsEmptyString(aLastLogId) ?

        <table width="100%">
          <tbody>
            <tr>
              <td width="50%" align="left"><Button variant="warning" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => SetCallLog(aLastLogId,aNumber,aCallInbound,aStartTimeISO8601,aAnsweredTime,aEndedTime,aRecognitionId,RecognitionType)}><img src={SavePng} style={{ height: '20px', paddingBottom: '2px'}}/> Update</Button></td>
              <td width="50%" align="right"><Button variant="info" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => OpenItemInSalesforce("Task", aLastLogId)}>View</Button></td>
            </tr>
          </tbody>
        </table> :

        <Button variant="info" className={GetBrandButtonClass(props.Brand)} style={{ width: '100%' }} onClick={() => SetCallLog("",aNumber,aCallInbound,aStartTimeISO8601,aAnsweredTime,aEndedTime,aRecognitionId,RecognitionType)}><img src={SavePng} style={{ height: '20px', paddingBottom: '2px'}}/> Register call</Button>
      }
    </div>
  }

  const PaintNotRecognized = () => 
  {
    return "Number not recognized"
  }

  const PaintSoftphoneSettingsLines = (aRecognitionId,aRecognition) => 
  {
    try
    {
      let Recognition = aRecognition.filter((item) => item.Id === aRecognitionId)
      if (Recognition.length > 0)
      {
        let ItemRecognition = Recognition[0]
        const SoftPhoneLayout = mSoftphoneLayout.Inbound.objects[ItemRecognition.RecordType]
        if ((SoftPhoneLayout !== undefined) && (SoftPhoneLayout !== null))
        {
          return <span className="smalltext" style={{ color: "navy", marginTop: "6px", marginBottom: "6px" }}>
            {SoftPhoneLayout.map((item) => <div>{item.displayName}: {ItemRecognition[item.apiName]}</div>)}
          </span>
        }
      }
    }
    catch { }

    return <span/>
  }

  const PaintWaitingForRecognition = () => 
  {
    return "Searching for recognition"
  }

  //START ACTIVE CODE

  let ActiveCall = null
  try
  {
    if (mInitialized && window.sforce)
    {
      try
      {
        const ActiveCallString = localStorage.getItem("newestcallevent")
        ActiveCall = !IsEmptyString(ActiveCallString) ? JSON.parse(ActiveCallString) : null
      }
      catch {}

      if ((ActiveCall !== null) && (!IsEmptyString(ActiveCall.CallId))) 
      {
        const lastcallid = localStorage.getItem("sforce_lastcallid") || ""
        if (ActiveCall.CallId !== lastcallid)
        {
          //tbd: update log description

          //Leave the last calldisposition, but reset the logmessage for the new call
          SetLogMessage("")

          if (localStorage.getItem("sforce_setting_showphone") === "1")
          {
              SalesforceAPI_showInConsole()
          }

          if (props.PrimaryTab)
          {
            const inbound = ActiveCall.Direction.toLowerCase() === "inbound"
            const number = inbound ? ActiveCall.CallerNumber : ActiveCall.CalledNumber
            localStorage.setItem("sforce_lastcallid", ActiveCall.CallId)
            localStorage.setItem("sforce_waitingforrecognition", "1") 
            localStorage.setItem("sforce_lastcallrecognition", "") 
            localStorage.setItem("sforce_lastcallrecognitionid", "")
            localStorage.setItem("sforce_lastcalllogid", "")
            localStorage.setItem("sforce_lastcalllogmessage", "")

            Repaint()
            PerformSearch(number, inbound) 
          }

          if (localStorage.getItem("sforce_setting_showconsoleonnewcall") === "1")
          {
            //tbd: only when softphone is not yet visible?
            SalesforceAPI_setSoftphonePanelVisibile()
          }
        }
        else if ((ActiveCall.CallId === lastcallid) && ((ActiveCall.State.toLowerCase() === "ended") || IsEmptyString(ActiveCall.State)))
        {
          const logid = localStorage.getItem("sforce_lastcalllogid")
          if (!IsEmptyString(logid))
          {
            //tbd: update
          }
        }
      }
    }
  }
  catch (error) { console.log("Preconfig 1 failed",error) }
  
  //Helper variables for repaint
  const callid = ActiveCall?.CallId ?? ""
  const callinbound = (ActiveCall?.Direction ?? "").toLowerCase() === "inbound"
  const callnumber = callinbound ? (ActiveCall?.CallerNumber ?? "") : (ActiveCall?.CalledNumber ?? "")  
  const calltime = LocalizedDateTime(ActiveCall?.StartTime ?? Date.now())
  let starttimeiso8601 = ""
  try { starttimeiso8601 = !IsEmptyString(ActiveCall?.StartTime) ? new Date(parseInt(ActiveCall.StartTime,10)).toISOString() : "" } catch {}
  let answeredtime = null
  try { answeredtime = !IsEmptyString(ActiveCall?.AnswerTime) ? parseInt(ActiveCall.AnswerTime,10) : null } catch {}
  let endedtime = null
  try { endedtime = !IsEmptyString(ActiveCall?.EndTime) ? parseInt(ActiveCall.EndTime,10) : null } catch {}

  const waitingforrecognition = localStorage.getItem("sforce_waitingforrecognition") === "1" 
  const recognitionid = localStorage.getItem("sforce_lastcallrecognitionid") ?? ""
  const lastlogid = localStorage.getItem("sforce_lastcalllogid") ?? "" 
  const error = !IsEmptyString(props.PhoneApiError) ? Translate("Failed to subscribe for phone events") + <br/> + Translate("Error") + ": " + props.PhoneApiError:
    mCallCenterSettings === null ? Translate("Failed to get Salesforce callcenter settings") : ""

  let recognition = []
  let recognitiontypes = {} //id=>type
  let recognitionnames = {} //id=>name
  try { 
    recognition = JSON.parse(localStorage.getItem("sforce_lastcallrecognition") ?? "") 
    recognition.map((item) => {
      recognitiontypes[item.Id] = item.RecordType
      recognitionnames[item.Id] = item.Name
    }) 
  } catch { }

  return (
    <table width="100%" height="100%">        
    <tbody>
    <tr height="24">
        <td width="33%" className={mSalesforceTab === "Search" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => SalesforceTabChange("Search")}><span className="up3px">{Translate("Search").toUpperCase()}</span></td>
        <td width="34%" className={mSalesforceTab === "History" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => SalesforceTabChange("History")}><span className="up3px">{Translate("History").toUpperCase()}</span></td>
        <td width="33%" className={mSalesforceTab === "Speed dial" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => SalesforceTabChange("Speed dial")}><span className="up3px">{Translate("Speed dial").toUpperCase()}</span></td>
    </tr>
    <tr height="*">

        {mSalesforceTab === "Search" ? 

            <td colSpan="3" valign="top">
              
              <table width="100%" height="100%" cellPadding="0" cellSpacing="0">
              <tbody>
                <tr height="30">
                  <td valign="top" align="left" className="padded10">
                    {!IsEmptyString(error) ? <div className="notificationerror">{error}</div> :
                    mCallCenterSettings === undefined ? <div className="notification">{Translate("Retrieving Salesforce callcenter settings")}</div> :
                    IsEmptyString(callid) ? <div className="notification">{Translate("No incoming call information available")}</div> :
                      <span>                          
                        <b>{(callinbound ? Translate("Incoming call from") : Translate("Outgoing call to")) + " " + callnumber}</b>
                        <br/>                                               
                        <div className="smalltext">{calltime}</div>
                        {PaintSoftphoneSettingsLines(recognitionid,recognition)}     
                        <CopyToClipboard text={callnumber}><button title="Copy number to clipboard" style={{ border: "0px"}}><img src={CopyPng} style={{ width: "16px"}}/></button></CopyToClipboard>
                        <button title="Call number" style={{ border: "0px"}} onClick={() => props.OnMakeCall(callnumber)}><img src={PhoneBlackPng} style={{ width: "16px"}}/></button>
                        <br/><br/>                   
                        {
                          recognition.length > 1 ? PaintMultipleRecognition(callnumber,callinbound,starttimeiso8601,answeredtime,endedtime,recognition,recognitionid,recognitiontypes,lastlogid) : 
                          recognition.length > 0 ? PaintSingleRecognition(callnumber,callinbound,starttimeiso8601,answeredtime,endedtime,recognitionid,recognitiontypes,recognitionnames,lastlogid) : 
                          waitingforrecognition ? PaintWaitingForRecognition() : 
                          PaintNotRecognized() 
                        }
                      </span>
                      } 
                  </td>
                </tr> 
                </tbody>
              </table>
            </td>

        : (mSalesforceTab === "History" ? 

            <td colSpan="3" align="left" valign="top">
              <CallHistory 
                Calls={props.Calls}
                QueryingRecognitionNumbers={props.RecognitionCacheQueryingNumbers}
                SpeedDials={((props.SpeedDials === undefined) || (props.SpeedDials === null) || !props.SpeedDials) ? [] : props.SpeedDials} 
                ShowHeader={false} 
                AddSearch={true}
                OnMakeCall={(number, name, application, scripts) => props.OnMakeCall(number, name, application, scripts)} 
                OnAddToSpeedDial={(number, name, application, scripts) => props.OnAddToSpeedDial(number, name, application, scripts)}
                OnRemoveFromSpeedDial={(number) => props.OnRemoveFromSpeedDial(number)} 
                OnRemoveFromCallHistory={(callid) => props.OnRemoveFromCallHistory(callid)}
                OnEmbeddedScreenpop={(url) => SalesforceScreenpopUrlExecute(url)}
              />
            </td>

        :

            <td colSpan="3" align="left" valign="top">                      
              <SpeedDials 
                SpeedDials={((props.SpeedDials === undefined) || (props.SpeedDials === null) || !props.SpeedDials) ? [] : props.SpeedDials} 
                ShowHeader={false} 
                OnMakeCall={(number, name, application, scripts) => props.OnMakeCall(number, name, application, scripts)} 
                OnRemoveFromSpeedDial={(number) => props.OnRemoveFromSpeedDial(number)}
                OnEmbeddedScreenpop={(url) => SalesforceScreenpopUrlExecute(url)}
              />
            </td>)
          }
    </tr>
    </tbody>
    </table>
  )
}

EmbeddedSalesforce.propTypes = {
  Calls: PropTypes.array,
  SecurityToken: PropTypes.string,
  PhoneApiError: PropTypes.string,
  LastCallEventId: PropTypes.string, //used for repainting purposes. updates on call event
  RecognitionCacheQueryingNumbers: PropTypes.array,
  SpeedDials: PropTypes.array,
  PrimaryTab: PropTypes.bool,
  Brand: PropTypes.string,
  OnAddToSpeedDial: PropTypes.func,
  OnMakeCall: PropTypes.func,
  OnRemoveFromCallHistory: PropTypes.func,
  OnRemoveFromSpeedDial: PropTypes.func,
  OnSetHistoryCallRecognition: PropTypes.func
}

export default EmbeddedSalesforce